import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Heart, Shield, Users, Sparkles, CheckCircle2 } from "lucide-react";

const TYPES = [
  { value: "pre-marriage", label: "Pre-Marriage Counselling", icon: Heart },
  { value: "marriage", label: "Marriage Counselling", icon: Users },
  { value: "financial", label: "Financial Counselling", icon: Shield },
  { value: "family", label: "Family Counselling", icon: Users },
  { value: "grief", label: "Grief / Bereavement", icon: Heart },
  { value: "general", label: "General / Spiritual", icon: Sparkles },
];

export default function Counselling() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "",
    counselling_type: "", preferred_contact: "email", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.counselling_type) return toast.error("Please choose a counselling type");
    setLoading(true);
    try {
      await api.post("/counselling", f);
      setDone(true);
      toast.success("Your enquiry has been received.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center" data-testid="counselling-success">
        <CheckCircle2 size={42} className="text-terracotta mx-auto" />
        <p className="overline text-terracotta mt-4">Received</p>
        <h1 className="font-heading font-black text-5xl tracking-tighter text-olive mt-3">We're here for you.</h1>
        <p className="mt-6 text-ink/75 text-lg">
          A pastoral counsellor will reach out to you within 48 hours in confidence.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="counselling-page">
      <section className="bg-cream border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="overline text-terracotta mb-4">Care</p>
          <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tighter text-olive leading-[0.95] max-w-3xl">
            Pastoral <em className="italic font-light text-terracotta">Counselling.</em>
          </h1>
          <p className="mt-6 text-ink/70 text-lg max-w-2xl">
            Life has seasons. Our pastoral counsellors are here to walk with you through marriage,
            finance, family, grief, and every other journey — in confidence.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="overline text-terracotta mb-3">What we offer</p>
          <h2 className="font-heading font-black text-3xl text-olive tracking-tight">A safe space, godly counsel.</h2>
          <ul className="mt-8 space-y-5">
            {TYPES.map((t) => (
              <li key={t.value} className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white border border-border flex items-center justify-center shrink-0">
                  <t.icon size={16} className="text-olive" />
                </div>
                <div>
                  <p className="font-heading font-bold text-lg text-ink">{t.label}</p>
                  <p className="text-sm text-muted_ink mt-0.5">
                    {t.value === "pre-marriage" && "Preparing hearts and homes for covenant."}
                    {t.value === "marriage" && "Strengthening marriages through every season."}
                    {t.value === "financial" && "Biblical stewardship and financial wisdom."}
                    {t.value === "family" && "Healing and growth for the whole household."}
                    {t.value === "grief" && "Walking with you through loss and healing."}
                    {t.value === "general" && "Spiritual direction and personal growth."}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="bg-white border border-border p-8 md:p-10 space-y-5">
            <p className="overline text-terracotta">Make an enquiry</p>
            <h3 className="font-heading font-black text-3xl text-olive tracking-tight">Reach out to us.</h3>
            <p className="text-sm text-muted_ink">All enquiries are handled in strict confidence.</p>

            <div className="grid md:grid-cols-2 gap-4 pt-2">
              <div>
                <Label>Full name *</Label>
                <Input required value={f.full_name} onChange={set("full_name")} data-testid="couns-name" className="mt-2 h-11" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" required value={f.email} onChange={set("email")} data-testid="couns-email" className="mt-2 h-11" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Phone *</Label>
                <Input required value={f.phone} onChange={set("phone")} data-testid="couns-phone" className="mt-2 h-11" />
              </div>
              <div>
                <Label>Preferred contact</Label>
                <Select value={f.preferred_contact} onValueChange={set("preferred_contact")}>
                  <SelectTrigger data-testid="couns-contact" className="mt-2 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone call</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Type of counselling *</Label>
              <Select value={f.counselling_type} onValueChange={set("counselling_type")}>
                <SelectTrigger data-testid="couns-type" className="mt-2 h-11">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message (optional)</Label>
              <Textarea rows={4} value={f.message} onChange={set("message")} data-testid="couns-msg" className="mt-2" />
            </div>
            <Button type="submit" disabled={loading} data-testid="couns-submit"
              className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none h-12 px-10 text-base font-semibold">
              {loading ? "Submitting…" : "Submit Enquiry"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
